// Mizan AI deep panels: Symbolic verifier, Pyodide sandbox, Octave/Simulink converter, Recipes library
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Play, Download, BookOpen, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";
import { useNavigate } from "react-router-dom";

export function SymbolicPanel({ accent, gradient }) {
  const [matlabExpr, setMatlabExpr] = useState("y = 3*x.^2 + 2*x - 5");
  const [pythonExpr, setPythonExpr] = useState("y = 3*x**2 + 2*x - 5");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mizan/verify/symbolic", { matlab_expr: matlabExpr, python_expr: pythonExpr });
      setResult(r.data);
    } catch (e) { toast.error("Verification failed"); }
    setBusy(false);
  };

  const Icon = result?.equivalent ? CheckCircle2 : (result ? XCircle : AlertTriangle);
  const color = result?.equivalent ? "#22c55e" : (result ? "#ef4444" : "#9ca3af");

  return (
    <div className="space-y-4" data-testid="mizan-symbolic">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Symbolic equivalence</div>
        <h2 className="font-serif text-3xl mt-1">Prove conversions are mathematically equivalent</h2>
        <div className="text-xs text-muted-foreground mt-1">Powered by SymPy. Tries symbolic simplification first, then falls back to numeric sampling.</div>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <div className="rounded-sm border border-border/60 bg-card p-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">MATLAB expression</div>
          <textarea value={matlabExpr} onChange={(e) => setMatlabExpr(e.target.value)} rows={4}
            data-testid="sym-matlab" className="w-full rounded-sm border border-border bg-background p-2 text-xs font-mono resize-none" />
        </div>
        <div className="rounded-sm border border-border/60 bg-card p-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Python expression</div>
          <textarea value={pythonExpr} onChange={(e) => setPythonExpr(e.target.value)} rows={4}
            data-testid="sym-python" className="w-full rounded-sm border border-border bg-background p-2 text-xs font-mono resize-none" />
        </div>
      </div>
      <button onClick={verify} disabled={busy} data-testid="sym-verify"
        className="h-10 px-4 rounded-sm text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
        style={{ background: gradient }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
        Verify equivalence
      </button>
      {result && (
        <div className="rounded-sm border border-border/60 bg-card p-4" data-testid="sym-result">
          <div className="flex items-center gap-3">
            <Icon className="h-6 w-6" style={{ color }} />
            <div>
              <div className="font-display text-lg" style={{ color }}>
                {result.equivalent ? "Equivalent" : "Not equivalent"}
              </div>
              <div className="text-xs text-muted-foreground">Method: {result.method}</div>
            </div>
          </div>
          <div className="mt-3 text-sm text-muted-foreground">{result.details}</div>
          {result.simplified_diff && result.simplified_diff !== "0" && (
            <div className="mt-3 text-xs font-mono bg-secondary/60 rounded-sm p-2">Residue: {result.simplified_diff}</div>
          )}
        </div>
      )}
    </div>
  );
}

export function ConvertPanel({ accent, gradient }) {
  const nav = useNavigate();
  const [src, setSrc] = useState(`% Octave: Step response of mass-spring-damper
m = 1; c = 0.5; k = 4;
A = [0 1; -k/m -c/m];
B = [0; 1/m];
sys = ss(A, B, [1 0], 0);
step(sys, 20);`);
  const [lang, setLang] = useState("octave");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);

  const convert = async () => {
    setBusy(true); setOut(null);
    try {
      const r = await api.post("/mizan/convert", { source_code: src, source_lang: lang, target_lang: "python" });
      setOut(r.data);
    } catch (e) {
      if (!handleQuotaError(e, nav)) toast.error(e?.response?.data?.detail || "Conversion failed");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="mizan-convert">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Convert</div>
        <h2 className="font-serif text-3xl mt-1">Octave / Simulink → Python</h2>
        <div className="text-xs text-muted-foreground mt-1">Idiomatic numpy / scipy / control output with key transformation notes.</div>
      </div>
      <div className="flex gap-2 items-center">
        <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="conv-lang" className="h-9 rounded-sm border border-border bg-background px-3 text-sm">
          <option value="octave">GNU Octave</option><option value="simulink">Simulink</option><option value="matlab">MATLAB</option>
        </select>
        <button onClick={convert} disabled={busy} data-testid="conv-run" className="h-9 px-4 rounded-sm text-white text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2" style={{ background: gradient }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Convert
        </button>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <textarea value={src} onChange={(e) => setSrc(e.target.value)} rows={12} data-testid="conv-src"
          className="w-full rounded-sm border border-border bg-background p-3 text-xs font-mono resize-none" />
        <div className="rounded-sm border border-border/60 bg-card p-3 min-h-[260px]">
          {out ? (
            <>
              <pre className="text-xs font-mono whitespace-pre-wrap" data-testid="conv-output">{out.converted_code}</pre>
              <div className="mt-3 text-xs text-muted-foreground">{out.explanation}</div>
              {(out.key_transformations || []).length > 0 && (
                <ul className="mt-2 list-disc list-inside text-xs space-y-0.5">
                  {out.key_transformations.map((k, i) => <li key={i}>{k}</li>)}
                </ul>
              )}
            </>
          ) : <div className="text-sm text-muted-foreground">Converted Python will appear here.</div>}
        </div>
      </div>
    </div>
  );
}

export function SandboxPanel({ accent, gradient }) {
  const [code, setCode] = useState(`# Live Python (Pyodide) — runs in your browser via WebAssembly
import numpy as np
xs = np.linspace(0, 2*np.pi, 100)
print("sin range:", np.sin(xs).min(), "→", np.sin(xs).max())
print("Sum of squares:", float(np.sum(xs**2)))`);
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const pyodideRef = useRef(null);

  const loadPyodide = async () => {
    if (pyodideRef.current) return pyodideRef.current;
    setBusy(true);
    setOut("Loading Pyodide WebAssembly runtime…");
    try {
      if (!window.loadPyodide) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://cdn.jsdelivr.net/pyodide/v0.25.0/full/pyodide.js";
          s.onload = res; s.onerror = rej; document.head.appendChild(s);
        });
      }
      const py = await window.loadPyodide({ indexURL: "https://cdn.jsdelivr.net/pyodide/v0.25.0/full/" });
      await py.loadPackage(["numpy"]);
      pyodideRef.current = py;
      setReady(true);
      setOut("Ready.");
      return py;
    } catch (e) {
      setOut(`Failed to load Pyodide: ${e.message || e}`);
      throw e;
    } finally { setBusy(false); }
  };

  const run = async () => {
    setBusy(true);
    try {
      const py = await loadPyodide();
      py.runPython("import sys, io; sys.stdout = io.StringIO(); sys.stderr = sys.stdout");
      try {
        await py.runPythonAsync(code);
        const stdout = py.runPython("sys.stdout.getvalue()");
        setOut(stdout || "(no output)");
      } catch (e) {
        setOut(`Error: ${e.message || e}`);
      }
    } catch {}
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="mizan-sandbox">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Sandbox</div>
        <h2 className="font-serif text-3xl mt-1">Live Python — in your browser</h2>
        <div className="text-xs text-muted-foreground mt-1">
          Runs Python 3.11 via Pyodide (WebAssembly). NumPy preloaded. {ready ? "Ready." : "Loads on first run."}
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-3 h-[500px]">
        <textarea value={code} onChange={(e) => setCode(e.target.value)} data-testid="sandbox-code"
          className="w-full h-full rounded-sm border border-border bg-background p-3 text-xs font-mono resize-none" />
        <div className="rounded-sm border border-border/60 bg-card p-3 overflow-y-auto">
          <pre className="text-xs font-mono whitespace-pre-wrap" data-testid="sandbox-output">{out || "Output will appear here…"}</pre>
        </div>
      </div>
      <button onClick={run} disabled={busy} data-testid="sandbox-run"
        className="h-10 px-4 rounded-sm text-white text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2"
        style={{ background: gradient }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run
      </button>
    </div>
  );
}

export function RecipesPanel({ accent, gradient }) {
  const [recipes, setRecipes] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/mizan/recipes").then((r) => setRecipes(r.data)).catch(() => {}); }, []);
  const open = async (name) => {
    const r = await api.get(`/mizan/recipes/${name}`);
    setActive(r.data);
  };

  return (
    <div className="space-y-4" data-testid="mizan-recipes">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Library</div>
        <h2 className="font-serif text-3xl mt-1 flex items-center gap-2"><BookOpen className="h-6 w-6" /> Engineering recipes</h2>
        <div className="text-xs text-muted-foreground mt-1">Production-ready Python snippets — copy, paste into Sandbox.</div>
      </div>
      <div className="grid lg:grid-cols-3 gap-3">
        <div className="space-y-1">
          {recipes.map((r) => (
            <button key={r.name} onClick={() => open(r.name)} data-testid={`recipe-${r.name}`}
              className={`w-full text-start rounded-sm border p-3 hover:border-foreground/30 transition-colors ${active?.name === r.name ? "border-foreground bg-card" : "border-border/60 bg-card/40"}`}>
              <div className="font-medium text-sm">{r.title}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{r.description}</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-2 rounded-sm border border-border/60 bg-card p-3">
          {active ? (
            <>
              <div className="font-display text-lg">{active.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{active.description}</div>
              <pre className="mt-3 text-xs font-mono bg-secondary/60 rounded-sm p-3 overflow-x-auto whitespace-pre" data-testid="recipe-code">{active.code}</pre>
              <button onClick={() => navigator.clipboard.writeText(active.code)} className="mt-3 text-xs underline-offset-2 hover:underline" data-testid="recipe-copy">Copy to clipboard</button>
            </>
          ) : <div className="text-sm text-muted-foreground">Pick a recipe to view code.</div>}
        </div>
      </div>
    </div>
  );
}
